/**
 * Clip Transition Engine (template_engine / editor preview)
 * =========================================================
 *
 * Clip-to-clip transitions for image (and other) timeline clips. A transition
 * plays at a clip's ENTRANCE (first `inDuration` seconds of its window) and/or
 * EXIT (last `outDuration` seconds). When two adjacent clips overlap, the
 * outgoing clip's exit and the incoming clip's entrance run simultaneously,
 * producing a cross-fade / slide / wipe between them.
 *
 * Mirror of video_worker/py_server/app/utils/transition_engine.py — keep math
 * identical so preview matches the render.
 *
 * item.transition = {
 *   in:  { type, duration },   // entrance transition
 *   out: { type, duration },   // exit transition
 *   // shorthand: { type, duration } applies to BOTH in and out
 * }
 *
 * Types: crossDissolve, slideLeft/Right/Up/Down, wipeLeft/Right/Up/Down,
 *        zoomIn, zoomOut, push, filmBurn, circularIris, irisClose.
 *
 * Entry: computeTransition(item, t, ctx) -> TransitionState
 *   { opacity, offsetX, offsetY, scale, clip, flash, active }
 *   clip = { insetTop, insetRight, insetBottom, insetLeft } fractions 0..1
 *          (fraction of each side hidden) — renderers map to clip-path / crop.
 */

const clamp01 = (v) => Math.max(0, Math.min(1, v))
const num = (v, d) => (typeof v === 'number' && !Number.isNaN(v) ? v : d)

const identityState = () => ({
  opacity: 1, offsetX: 0, offsetY: 0, scale: 1,
  clip: { insetTop: 0, insetRight: 0, insetBottom: 0, insetLeft: 0 },
  flash: 0, active: false,
})

const easeInOut = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2)

/**
 * Apply a single transition of `type` at phase p (0..1) in `dir` ('in'|'out').
 * For 'in', p goes 0 (fully transitioned, hidden/offset) -> 1 (settled).
 * For 'out', p goes 0 (settled) -> 1 (fully transitioned away).
 * We normalize so `prog` = how "present" the clip is (1 = fully on screen).
 */
const applyTransition = (state, type, p, dir, ctx) => {
  const w = (ctx && ctx.width) || 0
  const h = (ctx && ctx.height) || 0
  // prog = presence (1 = fully shown). For 'in', presence = p; for 'out', 1 - p.
  const prog = dir === 'in' ? p : 1 - p
  const e = easeInOut(prog)
  // amount = how far into the transition (1 = fully off), used for offsets.
  const off = 1 - e

  switch (type) {
    case 'crossDissolve':
    case 'fade':
      state.opacity *= e
      break
    case 'slideLeft': // enters from right / exits to left
      state.offsetX += (dir === 'in' ? 1 : -1) * off * w
      state.opacity *= dir === 'in' ? 1 : e
      break
    case 'slideRight':
      state.offsetX += (dir === 'in' ? -1 : 1) * off * w
      break
    case 'slideUp':
      state.offsetY += (dir === 'in' ? 1 : -1) * off * h
      break
    case 'slideDown':
      state.offsetY += (dir === 'in' ? -1 : 1) * off * h
      break
    case 'push': // like slide but paired clips push each other (same as slideLeft here)
      state.offsetX += (dir === 'in' ? 1 : -1) * off * w
      break
    case 'zoomIn': // grows into place (in) / grows past frame (out)
      state.scale *= dir === 'in' ? (0.6 + 0.4 * e) : (1 + off * 1.5)
      state.opacity *= e
      break
    case 'zoomOut': // shrinks from large (in) / shrinks away (out)
      state.scale *= dir === 'in' ? (1.6 - 0.6 * e) : (1 - off * 0.9)
      state.opacity *= e
      break
    case 'wipeLeft': // reveal boundary sweeps; hide a fraction of a side
      state.clip.insetRight = Math.max(state.clip.insetRight, off)
      break
    case 'wipeRight':
      state.clip.insetLeft = Math.max(state.clip.insetLeft, off)
      break
    case 'wipeUp':
      state.clip.insetBottom = Math.max(state.clip.insetBottom, off)
      break
    case 'wipeDown':
      state.clip.insetTop = Math.max(state.clip.insetTop, off)
      break
    case 'circularIris': // circular reveal grows (in) / shrinks (out)
      state.clip.circle = e // 0 = closed, 1 = fully open
      state.opacity *= Math.min(1, e * 2)
      break
    case 'irisClose':
      state.clip.circle = e
      break
    case 'filmBurn':
      state.opacity *= e
      state.flash = Math.max(state.flash, (1 - Math.abs(prog - 0.5) * 2)) // bright mid-transition
      break
    default:
      state.opacity *= e
      break
  }
}

/** Resolve the in/out transition configs (supports the shorthand). */
const resolveConfigs = (transition) => {
  if (!transition) return { inCfg: null, outCfg: null }
  let inCfg = transition.in || null
  let outCfg = transition.out || null
  // Shorthand { type, duration } => apply to both.
  if (!inCfg && !outCfg && transition.type) {
    inCfg = { type: transition.type, duration: transition.duration }
    outCfg = { type: transition.type, duration: transition.duration }
  }
  return { inCfg, outCfg }
}

/**
 * Compute the transition state for an item at absolute time t.
 * @param {object} item - the timeline item (reads item.transition + item.time)
 * @param {number} t    - absolute seconds
 * @param {object} ctx  - { width, height } of the clip box (for offsets)
 */
export const computeTransition = (item, t, ctx = {}) => {
  const state = identityState()
  const tr = item && item.transition
  if (!tr) return state
  const { inCfg, outCfg } = resolveConfigs(tr)
  if (!inCfg && !outCfg) return state

  const time = item.time || { start: 0, duration: 2 }
  const start = time.start || 0
  const duration = Math.max(0.0001, time.duration || 0)
  const end = start + duration
  const local = t - start

  // Entrance window: [0, inDuration].
  if (inCfg && inCfg.type) {
    const d = Math.min(num(inCfg.duration, 0.6), duration)
    if (local >= 0 && local < d) {
      const p = clamp01(local / d) // 0 -> 1 as it settles
      applyTransition(state, inCfg.type, p, 'in', ctx)
      state.active = true
    }
  }
  // Exit window: [duration - outDuration, duration].
  if (outCfg && outCfg.type) {
    const d = Math.min(num(outCfg.duration, 0.6), duration)
    const exitStart = duration - d
    if (local >= exitStart && local <= duration + 0.001) {
      const p = clamp01((local - exitStart) / d) // 0 -> 1 as it leaves
      applyTransition(state, outCfg.type, p, 'out', ctx)
      state.active = true
    }
  }
  return state
}

/** Build a CSS clip-path inset()/circle() string from a transition clip state. */
export const transitionClipPath = (clip) => {
  if (!clip) return undefined
  if (typeof clip.circle === 'number') {
    const r = (clip.circle * 75).toFixed(1) // 0..75% radius from center
    return `circle(${r}% at 50% 50%)`
  }
  const t = (clamp01(clip.insetTop) * 100).toFixed(1)
  const r = (clamp01(clip.insetRight) * 100).toFixed(1)
  const b = (clamp01(clip.insetBottom) * 100).toFixed(1)
  const l = (clamp01(clip.insetLeft) * 100).toFixed(1)
  if (+t === 0 && +r === 0 && +b === 0 && +l === 0) return undefined
  return `inset(${t}% ${r}% ${b}% ${l}%)`
}

export const TRANSITION_TYPES = [
  'crossDissolve', 'slideLeft', 'slideRight', 'slideUp', 'slideDown',
  'wipeLeft', 'wipeRight', 'wipeUp', 'wipeDown',
  'zoomIn', 'zoomOut', 'push', 'filmBurn', 'circularIris', 'irisClose',
]

export const isTransitionType = (type) => TRANSITION_TYPES.includes(type)

export default { computeTransition, transitionClipPath, isTransitionType, TRANSITION_TYPES }
